import React from "react";
import { Easing, interpolate, spring, useCurrentFrame } from "remotion";
import { LOOK } from "../../b2c/look";
import { Words } from "../../b2c/ui/Words";
import { MERCHANTS } from "../../merchant/merchants";
import { LogoTile } from "../../merchant/ui/Logo";
import { REEL, ReelFrame } from "../ui/ReelFrame";
import { BRANDS_REEL } from "./copy";

/**
 * Reel "Les marques" (9:16, silent, ~9.5s): how many brands are set up on
 * Frak. A big counter climbs to the live brand count, one tick per brand,
 * and each tick drops that brand's real logo into a grid. Then the hook
 * gives way to "Là, tes recommandations peuvent déjà payer.", and the end
 * card follows.
 *
 * The count and the logos are the live snapshot (`MERCHANTS`, = `BRANDS`
 * of `b2c/data/brands`): the video server re-renders when a brand goes
 * live. Frame 0 is the cover: hook, the final count and every logo; the
 * count restarts from 0 on frame `T.hold`. Silent: the sound is added in
 * Instagram.
 */

/** Timeline, in frames. Holds are reading time: never shorten one. */
const T = {
  /** The cover (final state) holds this long, then the count restarts. */
  hold: 8,
  /** The count climbs from 0 to the live count in `count` frames. */
  count: 84,
  /** The hook leaves; the line takes its place. */
  line: 100,
  exit: 178,
  end: 190,
  duration: 290,
} as const;

export const BRANDS_DURATION = T.duration;

const N = MERCHANTS.length;
const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ramp = (frame: number, at: number, len: number, ease = Easing.out(Easing.cubic)) =>
  interpolate(frame, [at, at + len], [0, 1], { ...clamp, easing: ease });

/** Layout (logical px, 1080×1920). Inside the Reels-safe band, clear of the right rail. */
const L = {
  number: { top: 470, size: 330 },
  unit: { top: 800, size: 68 },
  grid: { top: 930, height: 470, gap: 24, cols: 4, maxTile: 150, max: 16 },
} as const;
const CX = REEL.width / 2;

/** Frame on which logo `i` drops (the count reaches `i + 1`). */
const tickFrame = (i: number) => T.hold + ((i + 1) * T.count) / N;

/** Count shown at `frame`: the final one on the cover, then 0 → N. */
const countAt = (frame: number) =>
  frame < T.hold
    ? N
    : Math.min(N, Math.floor(((frame - T.hold) / T.count) * N + 1e-6));

const Grid: React.FC = () => {
  const frame = useCurrentFrame();
  const shown = MERCHANTS.slice(0, L.grid.max);
  const rows = Math.ceil(shown.length / L.grid.cols);
  const tile = Math.min(
    L.grid.maxTile,
    Math.floor((L.grid.height - (rows - 1) * L.grid.gap) / rows),
  );
  return (
    <>
      {shown.map((m, i) => {
        const row = Math.floor(i / L.grid.cols);
        const inRow = Math.min(L.grid.cols, shown.length - row * L.grid.cols);
        const col = i - row * L.grid.cols;
        const x =
          CX - (inRow * tile + (inRow - 1) * L.grid.gap) / 2 + col * (tile + L.grid.gap);
        const y = L.grid.top + row * (tile + L.grid.gap);
        const at = tickFrame(i);
        const pop =
          frame < T.hold
            ? 1
            : spring({
                frame: frame - at,
                fps: REEL.fps,
                config: { damping: 11, stiffness: 230, mass: 0.6 },
              });
        const visible = frame < T.hold || frame >= at;
        return (
          <LogoTile
            key={m.slug}
            m={m}
            size={tile}
            style={{
              position: "absolute",
              left: x,
              top: y,
              opacity: visible ? Math.min(1, pop * 3) : 0,
              scale: 0.4 + 0.6 * pop,
              translate: `0 ${(1 - pop) * 60}px`,
            }}
          />
        );
      })}
    </>
  );
};

const Counter: React.FC = () => {
  const frame = useCurrentFrame();
  const n = countAt(frame);
  // A punch on every tick, and a bigger one when the count lands.
  const last = frame < T.hold ? -99 : Math.max(...Array.from({ length: N }, (_, i) => tickFrame(i)).filter((t) => t <= frame), -99);
  const punch = 0.1 * (1 - ramp(frame, last, 7, Easing.out(Easing.quad)));
  const land = Math.sin(Math.PI * ramp(frame, T.hold + T.count, 14)) * 0.12;
  return (
    <>
      <div
        style={{
          position: "absolute",
          top: L.number.top,
          left: 0,
          width: REEL.width,
          textAlign: "center",
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: L.number.size,
          lineHeight: 1,
          letterSpacing: -L.number.size * 0.04,
          fontVariantNumeric: "tabular-nums",
          color: LOOK.white,
          scale: 1 + punch + land,
        }}
      >
        {n}
      </div>
      <div
        style={{
          position: "absolute",
          top: L.unit.top,
          left: 0,
          width: REEL.width,
          textAlign: "center",
          fontFamily: LOOK.display,
          fontWeight: LOOK.semi,
          fontSize: L.unit.size,
          lineHeight: 1,
          letterSpacing: -2,
          color: LOOK.sky,
        }}
      >
        {n === 1 ? BRANDS_REEL.unit.one : BRANDS_REEL.unit.other}
      </div>
    </>
  );
};

export const BrandsReel: React.FC = () => {
  // Thrown here, not at import: the bundle holds every video.
  if (N < 1) {
    throw new Error(
      "Brands reel: the snapshot has no live brand. Run `npm run sync:brands`.",
    );
  }
  const frame = useCurrentFrame();
  const out = ramp(frame, T.exit, 10, Easing.in(Easing.quad));

  return (
    <ReelFrame
      hook={BRANDS_REEL.hook}
      exitAt={T.line}
      end={{ at: T.end, line: BRANDS_REEL.end.line, sub: BRANDS_REEL.end.sub }}
    >
      <div style={{ position: "absolute", inset: 0, opacity: 1 - out, scale: 1 - 0.05 * out }}>
        <Counter />
        <Grid />
        {frame >= T.line ? (
          <Words
            text={BRANDS_REEL.line}
            start={T.line + 6}
            stagger={3}
            fontSize={68}
            color={LOOK.white}
            style={{ position: "absolute", top: 230, left: 110, right: 150 }}
          />
        ) : null}
      </div>
    </ReelFrame>
  );
};
